import { z } from 'zod';
import { REFUND_REASONS, returnLineInputSchema } from './refunds.js';
import { discountInputSchema } from './sales.js';

/**
 * Local-first devices (ADR-0112). The device contract is `docs/DEVICE_API.md`; these schemas are
 * the source of truth for its shapes.
 */

/** A device counts as online when it was seen less than this long ago. */
export const DEVICE_ONLINE_SECONDS = 120;

/**
 * The device's role, derived from its binding (ADR-0112 amendment 4; there is no stored role):
 * `primary` when a register (cash drawer) is bound to it — it holds that drawer's shift and cash
 * and prints the browser POS's receipts, so a branch has one primary per bound register;
 * `secondary` when unbound, which never opens a drawer or takes cash.
 */
const DEVICE_ROLES = ['primary', 'secondary'] as const;
export type DeviceRole = (typeof DEVICE_ROLES)[number];

/**
 * Registration, done once on the device by a manager (online): e-mail and password, then the
 * company and branch. Without `merchantId` / `branchId` the answer lists the choices.
 */
export const registerDeviceSchema = z.object({
  email: z.string().trim().toLowerCase().email().max(254),
  password: z.string().min(1).max(256),
  deviceName: z.string().trim().min(1).max(80),
  merchantId: z.uuid().optional(),
  branchId: z.uuid().optional(),
});

export type RegisterDevice = z.infer<typeof registerDeviceSchema>;

const registeredDeviceSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  merchantId: z.uuid(),
  branchId: z.uuid(),
  branchCode: z.string(),
  /** The device's permanent code in its branch (`D1`, `D2`, …, never reused); the receipt series is `{branch}-{code}-`. */
  code: z.string(),
  /** The cash drawer a manager bound to this device, or null (card / transfer sales only). */
  registerId: z.uuid().nullable(),
  registerCode: z.string().nullable(),
  registerName: z
    .object({ nameEn: z.string().nullable(), nameAr: z.string().nullable() })
    .nullable(),
  /** Derived: `primary` when bound to a register (drawer, cash, shift, browser receipts), else `secondary`. */
  role: z.enum(DEVICE_ROLES),
});
export type RegisteredDevice = z.infer<typeof registeredDeviceSchema>;

/** A branch where the person may register a device. */
const deviceBranchOptionSchema = z.object({
  id: z.uuid(),
  code: z.string(),
  nameEn: z.string().nullable(),
  nameAr: z.string().nullable(),
});

export const registerDeviceResultSchema = z.discriminatedUnion('status', [
  z.object({
    status: z.literal('select_branch'),
    /** Companies and branches where this person may register a device (`registers.manage`). */
    merchants: z.array(
      z.object({
        id: z.uuid(),
        nameEn: z.string().nullable(),
        nameAr: z.string().nullable(),
        branches: z.array(deviceBranchOptionSchema),
      }),
    ),
  }),
  z.object({
    status: z.literal('registered'),
    device: registeredDeviceSchema,
    /** The device's bearer token: shown only here, store it in the OS keychain. */
    token: z.string(),
  }),
]);
export type RegisterDeviceResult = z.infer<typeof registerDeviceResultSchema>;

/** A device as managers see it (`/manager/devices`). */
export const posDeviceSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  branchId: z.uuid(),
  code: z.string(),
  registerId: z.uuid().nullable(),
  registerCode: z.string().nullable(),
  registerName: z
    .object({ nameEn: z.string().nullable(), nameAr: z.string().nullable() })
    .nullable(),
  role: z.enum(DEVICE_ROLES),
  createdAt: z.string(),
  lastSeenAt: z.string().nullable(),
  lastSyncAt: z.string().nullable(),
  revokedAt: z.string().nullable(),
  online: z.boolean(),
});
export type PosDevice = z.infer<typeof posDeviceSchema>;

/**
 * `PUT /manager/devices/:id`: a manager binds the device to a register of its branch
 * (`registerId`, null unbinds; amendment 4) and the owner moves it to another branch. Nothing is
 * revoked: the device learns the change at its next sync. A move gives the device a new code (a
 * new receipt series) and needs it unbound; binding needs the registers' shifts closed.
 */
export const updatePosDeviceSchema = z
  .object({
    registerId: z.uuid().nullable().optional(),
    branchId: z.uuid().optional(),
  })
  .refine((v) => v.registerId !== undefined || v.branchId !== undefined, {
    error: 'Nothing to change.',
  });
export type UpdatePosDevice = z.infer<typeof updatePosDeviceSchema>;

/* Sync download: GET /device/sync?since=<version> (ADR-0112 §7a). */

/** Money on the device wire: integer fils as a decimal string (e.g. "1250" = 1.250 KWD). */
const minor = z.string().regex(/^-?\d+$/);

export const deviceSnapshotSchema = z.object({
  /** Opaque cursor: send it back as `since` next time. */
  version: z.string(),
  /** true: every collection is complete (replace local data); false: only changes since `since`. */
  full: z.boolean(),
  /** Latest catalog change; record it on every sale priced with this catalog. */
  catalogVersion: z.string(),
  serverTime: z.string(),
  offlineLimitHours: z.number().int(),
  device: registeredDeviceSchema,
  merchant: z.object({
    id: z.uuid(),
    nameEn: z.string().nullable(),
    nameAr: z.string().nullable(),
    currency: z.literal('KWD'),
    timezone: z.string(),
  }),
  branch: z.object({
    id: z.uuid(),
    code: z.string(),
    nameEn: z.string().nullable(),
    nameAr: z.string().nullable(),
    phone: z.string().nullable(),
    address: z.string().nullable(),
    receiptHeader: z.string().nullable(),
    receiptFooter: z.string().nullable(),
  }),
  /** The bound register (cash drawer), null on an unbound device (amendment 4). */
  register: z
    .object({
      id: z.uuid(),
      code: z.string(),
      nameEn: z.string().nullable(),
      nameAr: z.string().nullable(),
      defaultOpeningFloatMinor: minor,
      discrepancyToleranceMinor: minor,
    })
    .nullable(),
  /** Selling rules for `@arabat/pos-core` (always complete). */
  settings: z.object({
    maxDiscountPercent: z.string(),
    priceOverrideFloorPercent: z.string().nullable(),
    priceOverrideCeilingPercent: z.string().nullable(),
    priceOverrideBelowCost: z.boolean(),
    requireTenderReference: z.boolean(),
    blindCashCount: z.boolean(),
    cashOutOtherLimitMinor: minor,
    cashOutOtherShiftLimitMinor: minor,
    refundWindowDays: z.number().int(),
    refundApprovalLimitMinor: minor,
    refundOwnSalesNeedApproval: z.boolean(),
    allowNegativeStock: z.boolean(),
    /** Held sales are discarded after this many hours; 0 = never. */
    heldSaleExpiryHours: z.number().int(),
  }),
  /** The device's own counters as the server last saw them (if local data was lost, continue above). */
  counters: z.object({ lastReceiptNumber: z.string().nullable() }),
  /**
   * The shift the device works with (ADR-0112 amendments 3 and 4). A bound device: the latest
   * shift of its own register, open or closed (it learns here when a manager closed it from
   * `/manager`). An unbound device: the open shift it sells into, on the branch's main register
   * first, else the oldest open one. Null when there is none.
   */
  branchShift: z
    .object({
      id: z.uuid(),
      status: z.enum(['open', 'closed']),
      registerId: z.uuid(),
      registerCode: z.string(),
      openedAt: z.string(),
      closedAt: z.string().nullable(),
      openedBy: z.object({ membershipId: z.uuid(), userId: z.uuid(), fullName: z.string() }),
    })
    .nullable(),
  /**
   * A bound device of the branch: the one on the main register, else on the shift's register,
   * else the first registered. Null when no device of the branch is bound.
   */
  primaryDevice: z.object({ id: z.uuid(), name: z.string() }).nullable(),
  /** Always complete. */
  paymentMethods: z.array(
    z.object({
      id: z.uuid(),
      kind: z.string(),
      status: z.string(),
      availableInPos: z.boolean(),
      gatewayProvider: z.string().nullable(),
      financialAccountId: z.uuid().nullable(),
      nameEn: z.string().nullable(),
      nameAr: z.string().nullable(),
      feeType: z.enum(['none', 'fixed', 'percentage', 'fixed_plus_percentage']),
      feeFixedMinor: minor,
      feePercent: z.string(),
      sortOrder: z.number().int(),
    }),
  ),
  /** Always complete: active members who may use the POS at the branch. */
  staff: z.array(
    z.object({
      membershipId: z.uuid(),
      userId: z.uuid(),
      fullName: z.string(),
      isOwner: z.boolean(),
      /** argon2id PHC string; null = no PIN yet (cannot sign in on devices). */
      pinHash: z.string().nullable(),
      /** Permission keys held at this branch (`@arabat/permissions`). */
      permissions: z.array(z.string()),
    }),
  ),
  /**
   * Always complete: every discounted price at the point of sale (ADR-0133), running or
   * scheduled. The device replaces its set and sells at the offer in force (`offerAt`).
   */
  priceOffers: z
    .array(
      z.object({
        variantId: z.uuid(),
        discountId: z.uuid(),
        priceMinor: minor,
        startsAt: z.string().nullable(),
        endsAt: z.string().nullable(),
      }),
    )
    .default([]),
  /**
   * Always complete: the refunds the server holds (pending approval, processing or completed)
   * of this device's own sales at its branch, whatever the sale's age, wherever they were made (browser
   * POS or this device). The device adds its own refunds the server does not list yet, so what
   * is left to refund never counts a return twice (RS-09); kept for offline use until the next
   * sync.
   */
  saleRefunds: z
    .array(
      z.object({
        id: z.uuid(),
        saleId: z.uuid(),
        refundNumber: z.string(),
        status: z.string(),
        reason: z.string(),
        totalMinor: minor,
        createdAt: z.string(),
        lines: z.array(
          z.object({ position: z.number().int(), quantity: z.string(), amountMinor: minor }),
        ),
        payments: z.array(z.object({ paymentId: z.uuid(), amountMinor: minor })),
      }),
    )
    .default([]),
  /**
   * Always complete: what the server counts against each payment of this device's own sales
   * from refunds of OTHER sales — exchange credit of the sale paid back at the browser POS and
   * traced to (or through) the payment (BR-091, migration 0087). The device adds it to what
   * `saleRefunds` and its own refunds took, so it suggests what the server would. Absent from
   * an older server: none.
   */
  tracedPaybacks: z
    .array(z.object({ saleId: z.uuid(), paymentId: z.uuid(), amountMinor: minor }))
    .default([]),
  /** Always complete. */
  categories: z.array(
    z.object({
      id: z.uuid(),
      parentId: z.uuid().nullable(),
      nameEn: z.string().nullable(),
      nameAr: z.string().nullable(),
      sortOrder: z.number().int(),
      status: z.string(),
    }),
  ),
  /**
   * Products changed since `since` (all when `full`). `sellable` false = archived or no longer
   * sold at this branch: hide it, keep it for old receipts.
   */
  products: z.array(
    z.object({
      id: z.uuid(),
      categoryId: z.uuid().nullable(),
      nameEn: z.string().nullable(),
      nameAr: z.string().nullable(),
      type: z.string(),
      trackInventory: z.boolean(),
      sellable: z.boolean(),
      updatedAt: z.string(),
      variants: z.array(
        z.object({
          id: z.uuid(),
          nameEn: z.string().nullable(),
          nameAr: z.string().nullable(),
          sku: z.string().nullable(),
          priceMinor: minor,
          /** For the "not below cost" price-change rule. */
          costMinor: minor.nullable(),
          isDefault: z.boolean(),
          status: z.string(),
          barcodes: z.array(z.string()),
        }),
      ),
    }),
  ),
});
export type DeviceSnapshot = z.infer<typeof deviceSnapshotSchema>;

export const deviceSyncQuerySchema = z.object({ since: z.string().max(64).optional() });

export const deviceSettingsSchema = z.object({
  offlineLimitHours: z.number().int().min(1).max(720),
});
export type DeviceSettings = z.infer<typeof deviceSettingsSchema>;

/* Sync upload: POST /device/sync (ADR-0112 §5, §6, §7b, docs/DEVICE_API.md). */

/** Money on the device wire, never negative. */
const amountMinor = z.string().regex(/^\d{1,13}$/);
const note = z
  .string()
  .trim()
  .max(500)
  .nullish()
  .transform((v) => (v ? v : null));
const recordBase = {
  /** UUIDv7 made by the device: the same id on the device and the server, and the idempotency key. */
  id: z.uuid(),
  /** The device's clock when it happened (ISO 8601 with offset). */
  occurredAt: z.iso.datetime({ offset: true }),
  /** Who did it at the till (signed in with their PIN). */
  membershipId: z.uuid(),
};

const deviceShiftOpenSchema = z.object({
  kind: z.literal('shift_open'),
  ...recordBase,
  countedCashMinor: amountMinor,
  note,
});

const deviceCashMovementSchema = z.object({
  kind: z.literal('cash_movement'),
  ...recordBase,
  shiftId: z.uuid(),
  /** Pay-in / pay-out of the drawer ("other"); custody moves (safe drops, deposits) stay online. */
  direction: z.enum(['cash_in', 'cash_out']),
  amountMinor,
  note,
});

const deviceSaleSchema = z.object({
  kind: z.literal('sale'),
  ...recordBase,
  shiftId: z.uuid(),
  /** `{branch code}-{device code}-{6 digits}`, gap-free per device. */
  receiptNumber: z.string().regex(/^[A-Za-z0-9_-]{1,40}-\d{6}$/),
  /** `catalogVersion` of the snapshot the sale was priced with. */
  catalogVersion: z.string().max(64),
  lines: z
    .array(
      z.object({
        variantId: z.uuid(),
        quantity: z.string().regex(/^\d{1,9}(\.\d{1,3})?$/),
        /** The catalog price on the device (the discounted one while a discount is in force). */
        listUnitPriceMinor: amountMinor,
        /** The price before a product discount, and the discount (ADR-0133); absent without. */
        regularUnitPriceMinor: amountMinor.nullish().transform((v) => v ?? null),
        productDiscountId: z
          .uuid()
          .nullish()
          .transform((v) => v ?? null),
        /** The price charged (differs from the list price only for a price change). */
        unitPriceMinor: amountMinor,
        priceOverrideReason: note,
        discount: discountInputSchema.nullable(),
        grossMinor: amountMinor,
        lineDiscountMinor: amountMinor,
        orderDiscountMinor: amountMinor,
        netMinor: amountMinor,
      }),
    )
    .min(1)
    .max(200),
  orderDiscount: discountInputSchema.nullable(),
  subtotalMinor: amountMinor,
  lineDiscountsMinor: amountMinor,
  orderDiscountMinor: amountMinor,
  totalMinor: amountMinor,
  payments: z
    .array(
      z.object({
        id: z.uuid(),
        methodId: z.uuid(),
        amountMinor,
        tenderedMinor: amountMinor.nullable(),
        changeMinor: amountMinor,
        feeMinor: amountMinor,
        reference: z
          .string()
          .trim()
          .max(80)
          .nullish()
          .transform((v) => (v ? v : null)),
      }),
    )
    .max(10),
  /** The device decided a discount or price beyond the limit was allowed (pos-core). */
  aboveLimit: z.boolean(),
  zeroTotalReason: note,
  /** A supervisor who typed their PIN at the till to lend rights (BR-177). */
  override: z
    .object({
      approverMembershipId: z.uuid(),
      rights: z.array(z.string().max(64)).min(1).max(10),
      note,
    })
    .nullable(),
  /** The device has no printer: print on the branch's print agents (gap A). Default false. */
  requestRemotePrint: z.boolean().default(false),
});

const deviceRefundSchema = z.object({
  kind: z.literal('refund'),
  ...recordBase,
  shiftId: z.uuid(),
  /** A sale this device made (owner decision 4). */
  saleId: z.uuid(),
  lines: z.array(returnLineInputSchema).min(1).max(200),
  reason: z.enum(REFUND_REASONS),
  note,
  /** Pay-back per original payment (pos-core `paybackAllocation`). */
  payback: z
    .array(
      z.object({
        paymentId: z.uuid(),
        amountMinor,
        reference: z
          .string()
          .trim()
          .max(80)
          .nullish()
          .transform((v) => (v ? v : null)),
      }),
    )
    .max(10),
  /** Second person who approved at the till (pos-core `refundApprovalReasons`), if any. */
  approval: z
    .object({
      approverMembershipId: z.uuid(),
      reasons: z.array(z.enum(['over_limit', 'outside_window', 'own_sale'])).min(1),
    })
    .nullable(),
});

const deviceShiftCloseSchema = z.object({
  kind: z.literal('shift_close'),
  ...recordBase,
  shiftId: z.uuid(),
  countedCashMinor: amountMinor,
  note,
  /**
   * A manager closed the shift of an absent cashier at the till with their own PIN (ADR-0112
   * amendment 3): `membershipId` is the manager, who needs `registers.manage` at the branch;
   * `note` is required. Recorded as a forced close and listed for review.
   */
  forced: z.boolean().default(false),
});

/** A cashier signed in on the device with their PIN (their "last sign-in", audited). */
const deviceSignInSchema = z.object({
  kind: z.literal('sign_in'),
  ...recordBase,
});

/**
 * A numbered copy of a receipt printed again on the device (BR-182, CD-13): written to the
 * reprint log and the audit log like a copy from the browser till.
 */
const deviceReprintSchema = z.object({
  kind: z.literal('reprint'),
  ...recordBase,
  /** A sale this device made and uploaded before this record. */
  saleId: z.uuid(),
  /** The shift open on the device when the copy was printed, if any. */
  shiftId: z
    .uuid()
    .nullish()
    .transform((v) => v ?? null),
  /** The copy number printed on the receipt ("نسخة رقم n"), counted on the device from 1. */
  copyNumber: z.number().int().min(1).max(10_000),
});

export const deviceRecordSchema = z.discriminatedUnion('kind', [
  deviceShiftOpenSchema,
  deviceCashMovementSchema,
  deviceSaleSchema,
  deviceRefundSchema,
  deviceShiftCloseSchema,
  deviceSignInSchema,
  deviceReprintSchema,
]);
export const DEVICE_RECORD_KINDS = [
  'shift_open',
  'sale',
  'cash_movement',
  'refund',
  'shift_close',
  'sign_in',
  'reprint',
] as const;
export type DeviceRecord = z.infer<typeof deviceRecordSchema>;

/**
 * Records in causal order (shift open → sales / cash movements / refunds → shift close). Each
 * record is checked on its own, so one bad record does not block the others.
 */
export const deviceUploadSchema = z.object({
  records: z
    .array(
      z
        .object({ kind: z.string().max(32), id: z.uuid(), occurredAt: z.string().max(64) })
        .passthrough(),
    )
    .min(1)
    .max(200),
});

const deviceRecordResultSchema = z.object({
  id: z.uuid(),
  kind: z.string(),
  status: z.enum(['accepted', 'duplicate', 'refused']),
  /** Accepted: what the server created (e.g. the refund number). */
  result: z.record(z.string(), z.unknown()).nullable(),
  /** Refused: an `ErrorCode` and a message. */
  code: z.string().nullable(),
  message: z.string().nullable(),
  /** The record's device time looked wrong; accepted anyway and shown to managers (gap B). */
  clockSkew: z.boolean(),
});
export type DeviceRecordResult = z.infer<typeof deviceRecordResultSchema>;

export const deviceUploadResultSchema = z.object({
  serverTime: z.string(),
  results: z.array(deviceRecordResultSchema),
});
export type DeviceUploadResult = z.infer<typeof deviceUploadResultSchema>;

/* Device exceptions for managers (ADR-0112 §5, gap B). */

/**
 * `negative_stock`, `clock_skew`, `refused` (ADR-0112 §5, gap B); amendment 3 adds
 * `shift_left_open` (the person who opened a device shift still had an open shift elsewhere),
 * `late` (a record attached to a branch shift that was already closed) and `forced_close` (a
 * manager closed an absent cashier's shift at the till).
 */
const DEVICE_EXCEPTION_KINDS = [
  'negative_stock',
  'clock_skew',
  'refused',
  'shift_left_open',
  'late',
  'forced_close',
] as const;

export const deviceExceptionQuerySchema = z.object({
  status: z.enum(['open', 'resolved']).default('open'),
});

export const deviceExceptionSchema = z.object({
  id: z.uuid(),
  kind: z.enum(DEVICE_EXCEPTION_KINDS),
  branchId: z.uuid(),
  deviceId: z.uuid(),
  deviceName: z.string(),
  /** The code of the register the device is bound to; the device's own code once unbound. */
  registerCode: z.string(),
  recordId: z.uuid(),
  recordKind: z.string(),
  /** negative_stock: variantId, productName, balance, receiptNumber; clock_skew: occurredAt,
   *  receivedAt, outsideShift; refused: code, message; shift_left_open: staleShiftId,
   *  staleRegisterId, staleRegisterCode, staleBranchId, staleOpenedAt, staleOnDevice,
   *  staleShiftClosed, fullName; late: shiftId, closedAt, recordKind; forced_close: shiftId,
   *  absentMembershipId, absentName, note. */
  details: z.record(z.string(), z.unknown()),
  occurredAt: z.string(),
  receivedAt: z.string(),
  createdAt: z.string(),
  resolvedAt: z.string().nullable(),
  resolvedByName: z.string().nullable(),
  resolutionNote: z.string().nullable(),
  /** Refused records only: what the device sent, kept so nothing is lost. */
  payload: z.record(z.string(), z.unknown()).nullable(),
});
export type DeviceException = z.infer<typeof deviceExceptionSchema>;

export const resolveDeviceExceptionSchema = z.object({
  note: z.string().trim().min(1).max(500),
});

/* Self-service PINs (ADR-0112, owner decision 6): asked from the till, set through an e-mailed link. */

export const devicePinResetRequestSchema = z.object({ membershipId: z.uuid() });
export const devicePinResetCompleteSchema = z.object({
  token: z.string().min(20).max(200),
  pin: z.string().regex(/^\d{6,12}$/),
});
